import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

export default class extends Controller {
  static targets = ["input", "feedback", "results", "hidden", "name", "ie", "im"]
  static values = {
    mask: { type: String, default: "cnpj" },
    validation: { type: String, default: "cnpj" },
    url: { type: String, default: "" }
  }

  connect() {
    this.boundHandlers = new Map()
    this.setupEventListeners()
    this.setupAutocomplete()
  }

  disconnect() {
    this.removeEventListeners()
    this.clearAutocomplete()
    this.boundHandlers.clear()
  }

  setupEventListeners() {
    if (this.hasInputTarget) {
      this.inputTargets.forEach((input, index) => {
        if (input && input.addEventListener) {
          // Store bound handlers for proper cleanup
          const boundInputHandler = this.handleInput.bind(this)
          const boundBlurHandler = this.handleBlur.bind(this)
          const boundFocusHandler = this.handleFocus.bind(this)
          const boundKeydownHandler = this.handleKeydown.bind(this)

          this.boundHandlers.set(`input_${index}`, boundInputHandler)
          this.boundHandlers.set(`blur_${index}`, boundBlurHandler)
          this.boundHandlers.set(`focus_${index}`, boundFocusHandler)
          this.boundHandlers.set(`keydown_${index}`, boundKeydownHandler)

          input.addEventListener('input', boundInputHandler)
          input.addEventListener('blur', boundBlurHandler)
          input.addEventListener('focus', boundFocusHandler)
          input.addEventListener('keydown', boundKeydownHandler)
        }
      })
    }
  }

  removeEventListeners() {
    if (this.hasInputTarget) {
      this.inputTargets.forEach((input, index) => {
        if (input && input.removeEventListener) {
          const boundInputHandler = this.boundHandlers.get(`input_${index}`)
          const boundBlurHandler = this.boundHandlers.get(`blur_${index}`)
          const boundFocusHandler = this.boundHandlers.get(`focus_${index}`)
          const boundKeydownHandler = this.boundHandlers.get(`keydown_${index}`)

          if (boundInputHandler) input.removeEventListener('input', boundInputHandler)
          if (boundBlurHandler) input.removeEventListener('blur', boundBlurHandler)
          if (boundFocusHandler) input.removeEventListener('focus', boundFocusHandler)
          if (boundKeydownHandler) input.removeEventListener('keydown', boundKeydownHandler)
        }
      })
    }
  }

  setupAutocomplete() {
    Logger.debug('Setting up autocomplete')
    // Get autocomplete URL from data attribute or value
    if (this.hasUrlValue) {
      this.url = this.urlValue
      Logger.debug('Autocomplete URL from value', { url: this.url })
    } else {
      const input = this.inputTargets[0]
      if (input) {
        const container = input.closest('[data-autocomplete-url]')
        if (container) {
          this.url = container.dataset.url
          Logger.debug('Autocomplete URL from container', { url: this.url })
        } else {
          Logger.warn('No container with data-autocomplete-url found')
        }
      } else {
        Logger.warn('No input target found')
      }
    }

    // Validate autocomplete URL
    if (!this.url) {
      Logger.warn('CNPJ Controller: No autocomplete URL provided')
    } else {
      Logger.debug('Autocomplete URL set', { url: this.url })
    }
  }

  handleInput(event) {
    Logger.debug('Input event fired', { value: event.target.value })
    const input = event.target
    if (input) {
      this.applyMask(input)
      this.clearValidationState(input)
      Logger.debug('About to call handleAutocompleteInput')
      this.handleAutocompleteInput(input)
    }
  }

  handleBlur(event) {
    const input = event.target
    if (input) {
      this.validateInput(input)
      // Delay hiding results to allow for clicks
      setTimeout(() => this.hideResults(), 200)
    }
  }

  handleFocus(event) {
    const input = event.target
    if (input) {
      this.clearValidationState(input)
      if (input.value.length > 0) {
        this.showResults()
      }
    }
  }

  handleKeydown(event) {
    const input = event.target
    if (!input) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateResults('down')
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateResults('up')
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentResult()
        break
      case 'Escape':
        this.hideResults()
        input.blur()
        break
    }
  }

  handleAutocompleteInput(input) {
    Logger.debug('Handling autocomplete input', { value: input.value })
    if (!this.url) {
      Logger.warn('No autocomplete URL available')
      return
    }

    const cleanValue = this.getCleanValue(input)
    Logger.debug('Clean value for search', { value: cleanValue })

    if (cleanValue.length >= 2) {
      Logger.debug('Triggering autocomplete search', { value: cleanValue })
      this.fetchAutocompleteResults(cleanValue)
    } else {
      Logger.debug('Value too short, hiding results')
      this.hideResults()
    }
  }

  async fetchAutocompleteResults(query) {
    Logger.debug('Fetching autocomplete results', { query })
    if (!this.url) {
      Logger.warn('No autocomplete URL available for fetch')
      return
    }

    try {
      // Add loading state
      Logger.debug('Showing loading state')
      this.showLoadingState()

      const fullUrl = `${this.url}?q=${encodeURIComponent(query)}`
      Logger.debug('Making request', { url: fullUrl })

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      })

      Logger.debug('Response received', { status: response.status, statusText: response.statusText })

      if (response.ok) {
        const data = await response.json()
        Logger.debug('Data received', { data })
        this.displayResults(data)
      } else {
        Logger.error('Autocomplete request failed', { status: response.status })
        this.hideResults()
        this.showErrorState('Erro na busca. Tente novamente.')
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        Logger.error('Autocomplete request timeout')
        this.showErrorState('Tempo limite excedido. Tente novamente.')
      } else {
        Logger.error('Autocomplete error', error)
        this.showErrorState('Erro na conexão. Verifique sua internet.')
      }
      this.hideResults()
    } finally {
      Logger.debug('Hiding loading state')
      this.hideLoadingState()
    }
  }

  displayResults(data) {
    if (!this.hasResultsTarget || !Array.isArray(data) || data.length === 0) {
      this.hideResults()
      return
    }

    const resultsContainer = this.resultsTargets[0]
    if (!resultsContainer) return

    // Clear existing results
    resultsContainer.innerHTML = ''

    // Create result items
    data.forEach((item, index) => {
      const resultItem = document.createElement('li')
      resultItem.className = 'dropdown-item'
      resultItem.setAttribute('data-index', index)
      resultItem.setAttribute('data-value', item.value || item.id || item)
      resultItem.setAttribute('data-label', item.label || item.name || item)

      // item.label/item.name come from the server autocomplete response — textContent, never
      // innerHTML, so a crafted provider/entity name can't inject markup.
      resultItem.textContent = item.label || item.name || item

      resultItem.addEventListener('click', () => this.selectResult(item, index))
      resultItem.addEventListener('mouseenter', () => this.highlightResult(index))

      resultsContainer.appendChild(resultItem)
    })

    this.showResults()
    this.currentResults = data
    this.selectedIndex = -1
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTargets.forEach(container => {
        container.style.display = 'block'
        container.classList.add('show')
      })
    }
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTargets.forEach(container => {
        container.style.display = 'none'
        container.classList.remove('show')
      })
    }
    this.currentResults = null
    this.selectedIndex = -1
  }

  navigateResults(direction) {
    if (!this.currentResults || this.currentResults.length === 0) return

    const items = this.resultsTargets[0]?.querySelectorAll('.dropdown-item')
    if (!items || items.length === 0) return

    // Remove previous highlight
    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove('active')
    }

    if (direction === 'down') {
      this.selectedIndex = this.selectedIndex < this.currentResults.length - 1 ? this.selectedIndex + 1 : 0
    } else {
      this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.currentResults.length - 1
    }

    // Add highlight to current item
    if (items[this.selectedIndex]) {
      items[this.selectedIndex].classList.add('active')
      items[this.selectedIndex].scrollIntoView({ block: 'nearest' })
    }
  }

  highlightResult(index) {
    if (!this.hasResultsTarget) return

    const items = this.resultsTargets[0]?.querySelectorAll('.dropdown-item')
    if (!items) return

    // Remove previous highlight
    items.forEach(item => item.classList.remove('active'))

    // Add highlight to current item
    if (items[index]) {
      items[index].classList.add('active')
      this.selectedIndex = index
    }
  }

  selectResult(item, index) {
    const input = this.inputTargets[0]
    if (!input) return

    // Set the input value with mask
    const cleanValue = item.value || item.id || item
    input.value = this.formatWithMask(cleanValue)

    // Set hidden field value if available
    if (this.hasHiddenTarget) {
      this.hiddenTargets.forEach(hidden => {
        if (hidden.name.includes('entity_id') || hidden.name.includes('provider_id')) {
          hidden.value = item.id || item.value || cleanValue
        }
      })
    }

    // Auto-fill other fields if available
    if (this.hasNameTarget && item.name) {
      this.nameTarget.value = item.name
    }

    if (this.hasIeTarget && item.ie_attr) {
      this.ieTarget.value = item.ie_attr
    }

    if (this.hasImTarget && item.im_attr) {
      this.imTarget.value = item.im_attr
    }

    // Trigger change event
    input.dispatchEvent(new Event('change', { bubbles: true }))

    // Hide results
    this.hideResults()

    // Dispatch entity-selected event for other controllers (like address)
    window.dispatchEvent(new CustomEvent('entity-selected', {
      detail: item,
      bubbles: true
    }))

    // Validate the input
    this.validateInput(input)
  }

  selectCurrentResult() {
    if (this.selectedIndex >= 0 && this.currentResults && this.currentResults[this.selectedIndex]) {
      this.selectResult(this.currentResults[this.selectedIndex], this.selectedIndex)
    }
  }

  clearAutocomplete() {
    this.hideResults()
    this.currentResults = null
    this.selectedIndex = -1
  }

  formatWithMask(cleanValue) {
    // Format clean value with CNPJ mask
    if (!cleanValue || typeof cleanValue !== 'string') return ''

    let value = cleanValue.replace(/\D/g, '')

    if (value.length <= 2) {
      return value
    } else if (value.length <= 5) {
      return value.substring(0, 2) + '.' + value.substring(2)
    } else if (value.length <= 8) {
      return value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5)
    } else if (value.length <= 12) {
      return value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5, 8) + '/' + value.substring(8)
    } else {
      return value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5, 8) + '/' + value.substring(8, 12) + '-' + value.substring(12, 14)
    }
  }

  applyMask(input) {
    if (!input || typeof input.value !== 'string') return

    let value = input.value.replace(/\D/g, '') // Remove non-digits

    // Apply CNPJ mask: XX.XXX.XXX/XXXX-XX
    if (value.length <= 2) {
      value = value
    } else if (value.length <= 5) {
      value = value.substring(0, 2) + '.' + value.substring(2)
    } else if (value.length <= 8) {
      value = value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5)
    } else if (value.length <= 12) {
      value = value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5, 8) + '/' + value.substring(8)
    } else {
      value = value.substring(0, 2) + '.' + value.substring(2, 5) + '.' + value.substring(5, 8) + '/' + value.substring(8, 12) + '-' + value.substring(12, 14)
    }

    input.value = value
  }

  validateInput(input) {
    if (!input || typeof input.value !== 'string') return

    const cnpj = input.value.replace(/\D/g, '')

    if (cnpj.length === 0) {
      return // Don't validate empty fields
    }

    const isValid = this.validateCNPJ(cnpj)

    if (isValid) {
      this.setValidState(input)
    } else {
      this.setInvalidState(input)
    }
  }

  validateCNPJ(cnpj) {
    if (!cnpj || typeof cnpj !== 'string' || cnpj.length !== 14) return false

    // Check if all digits are the same
    if (/^(\d)\1+$/.test(cnpj)) return false

    try {
      // Validate first digit
      let sum = 0
      let weight = 2

      for (let i = 11; i >= 0; i--) {
        sum += parseInt(cnpj.charAt(i)) * weight
        weight = weight === 9 ? 2 : weight + 1
      }

      let digit = 11 - (sum % 11)
      if (digit > 9) digit = 0

      if (parseInt(cnpj.charAt(12)) !== digit) return false

      // Validate second digit
      sum = 0
      weight = 2

      for (let i = 12; i >= 0; i--) {
        sum += parseInt(cnpj.charAt(i)) * weight
        weight = weight === 9 ? 2 : weight + 1
      }

      digit = 11 - (sum % 11)
      if (digit > 9) digit = 0

      return parseInt(cnpj.charAt(13)) === digit
    } catch (error) {
      Logger.error('CNPJ validation error', error)
      return false
    }
  }

  setValidState(input) {
    if (!input) return

    // Bootstrap 4 validation classes
    input.classList.remove('is-invalid')
    input.classList.add('is-valid')

    // Add Bootstrap 4 was-validated class to form
    this.addWasValidatedClass(input)

    this.hideFeedback(input)
  }

  setInvalidState(input) {
    if (!input) return

    // Bootstrap 4 validation classes
    input.classList.remove('is-valid')
    input.classList.add('is-invalid')

    // Add Bootstrap 4 was-validated class to form
    this.addWasValidatedClass(input)

    this.showFeedback(input)
  }

  clearValidationState(input) {
    if (!input) return

    // Bootstrap 4 validation classes
    input.classList.remove('is-valid', 'is-invalid')
    this.hideFeedback(input)
  }

  addWasValidatedClass(input) {
    // Add Bootstrap 4 was-validated class to the form
    const form = input.closest('form')
    if (form) {
      form.classList.add('was-validated')
    }
  }

  showFeedback(input) {
    const feedback = this.findFeedback(input)
    if (feedback) {
      // Bootstrap 4 show class
      feedback.classList.add('show')
      feedback.style.display = 'block'
    }
  }

  hideFeedback(input) {
    const feedback = this.findFeedback(input)
    if (feedback) {
      // Bootstrap 4 hide class
      feedback.classList.remove('show')
      feedback.style.display = 'none'
    }
  }

  findFeedback(input) {
    if (!input) return null

    try {
      // Look for feedback in the same container
      const container = input.closest('.dropdown') || input.parentElement
      if (container) {
        return container.querySelector('.invalid-feedback')
      }
    } catch (error) {
      Logger.error('Error finding feedback element', error)
    }
    return null
  }

  // Public method for external validation
  validate() {
    if (!this.hasInputTarget) return true

    let isValid = true

    this.inputTargets.forEach(input => {
      if (input && input.value) {
        const cnpj = input.value.replace(/\D/g, '')
        if (!this.validateCNPJ(cnpj)) {
          this.setInvalidState(input)
          isValid = false
        }
      }
    })

    return isValid
  }

  // Public method to get clean CNPJ value
  getCleanValue(input) {
    if (!input || typeof input.value !== 'string') return ''
    return input.value.replace(/\D/g, '')
  }

  // Method to reset validation state
  resetValidation() {
    if (this.hasInputTarget) {
      this.inputTargets.forEach(input => {
        this.clearValidationState(input)
      })
    }

    // Remove was-validated class from form
    const form = this.element.closest('form')
    if (form) {
      form.classList.remove('was-validated')
    }
  }

  showLoadingState() {
    if (this.hasResultsTarget) {
      this.resultsTargets.forEach(container => {
        container.classList.add('loading')
        container.innerHTML = '<li class="dropdown-item disabled">Carregando...</li>'
        container.style.display = 'block'
      })
    }
  }

  hideLoadingState() {
    if (this.hasResultsTarget) {
      this.resultsTargets.forEach(container => {
        container.classList.remove('loading')
      })
    }
  }

  showErrorState(message) {
    if (this.hasResultsTarget) {
      this.resultsTargets.forEach(container => {
        container.innerHTML = `<li class="dropdown-item disabled text-danger">${message}</li>`
        container.style.display = 'block'

        // Auto-hide error after 3 seconds
        setTimeout(() => this.hideResults(), 3000)
      })
    }
  }
}
